import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import { EASE_OUT_EXPO } from '../../components/AnimatedSection';
import useLatestVideo from '../../hooks/useLatestVideo';
import './SermonSection.scss';
import fallbackThumbnail from '../../assets/images/sermon/video-thumbnail.jpg';

const SermonSection = () => {
    const { t } = useTranslation();
    const { videoUrl, channelUrl, thumbnail, title } = useLatestVideo();
    const prefersReducedMotion = useReducedMotion();

    const thumbnailSrc = thumbnail || fallbackThumbnail;
    const thumbnailAlt = title || t('watch_latest_sermon');

    const fadeUp = (delay, duration = 1.0) =>
        prefersReducedMotion
            ? {}
            : {
                  initial: { opacity: 0, y: 20 },
                  whileInView: { opacity: 1, y: 0 },
                  viewport: { once: true, amount: 0.25 },
                  transition: { duration, delay, ease: EASE_OUT_EXPO },
              };

    const scaleReveal = prefersReducedMotion
        ? {}
        : {
              initial: { opacity: 0, scale: 0.98 },
              whileInView: { opacity: 1, scale: 1 },
              viewport: { once: true, amount: 0.25 },
              transition: { duration: 1.2, delay: 0.3, ease: EASE_OUT_EXPO },
          };

    return (
        <section className="sermon-section" aria-labelledby="sermon-title">
            <div className="sermon-section__container">
                <motion.span className="sermon-section__label" {...fadeUp(0)}>
                    {t('powerful_life_changing')}
                </motion.span>

                <div className="sermon-section__grid">
                    <div className="sermon-section__content">
                        <motion.h2
                            id="sermon-title"
                            className="sermon-section__title"
                            {...fadeUp(0, 1.1)}
                        >
                            {t('watch_latest_sermon')}
                        </motion.h2>

                        <motion.div className="sermon-section__cta" {...fadeUp(0.4, 0.9)}>
                            <a
                                href={channelUrl}
                                className="button button--outline-blue sermon-section__cta-button"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t('sermons_archive')}
                            </a>
                        </motion.div>
                    </div>

                    <motion.div className="sermon-section__video" {...scaleReveal}>
                        <a
                            href={videoUrl}
                            className="sermon-section__thumbnail-link"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img
                                src={thumbnailSrc}
                                alt={thumbnailAlt}
                                className="sermon-section__thumbnail"
                                loading="lazy"
                            />
                            <div className="sermon-section__play-button">
                                <svg className="sermon-section__play-icon" viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M8 5v14l11-7z" fill="currentColor" />
                                </svg>
                            </div>
                        </a>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default SermonSection;
